import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Table, Input, Typography, Row, Col, Card, Alert, Spin, Skeleton, Tag, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { useApolloClient } from '@apollo/client';
import { useCandidatesList } from '../hooks/useCandidatesList';
import { useCandidateSearchTotals } from '../hooks/useCandidateSearchTotals';
import { createCandidateListColumns } from '../utils/tableColumns';
import { LIST_CANDIDATES_QUERY } from '../queries/candidateQueries';

const { Title, Text } = Typography;

function CandidatesListPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const history = useHistory();
    const company = useSelector((state) => state.user.currentUser.company);
    const companyId = company?.id;
    const apolloClient = useApolloClient();

    const { loading, error, candidates, totalCount, pageCount, refetch } = useCandidatesList(
        companyId,
        pageNumber,
        pageSize,
        debouncedSearchTerm
    );

    const { 
        loading: totalsLoading,
        totalCandidates: totalCandidatesFromTotals, 
        totalReferrals, 
        totalApplications 
    } = useCandidateSearchTotals(companyId);

    useEffect(() => {
        if (!companyId || loading || !pageCount) return;

        const pagesToPreload = [];

        if (pageNumber < pageCount) {
            pagesToPreload.push(pageNumber + 1);
        }

        if (pageNumber + 1 < pageCount) {
            pagesToPreload.push(pageNumber + 2);
        }

        if (pageNumber > 1) {
            pagesToPreload.push(pageNumber - 1);
        }

        pagesToPreload.forEach(page => {
            apolloClient.query({
                query: LIST_CANDIDATES_QUERY,
                variables: {
                    companyId,
                    pageNumber: page,
                    pageSize,
                    query: debouncedSearchTerm || ''
                },
                fetchPolicy: 'cache-first',
                errorPolicy: 'ignore'
            }).catch(() => {

            });
        });
    }, [apolloClient, companyId, pageNumber, pageSize, debouncedSearchTerm, pageCount, loading]);

    const debouncedSearch = useCallback(
        debounce((value) => {
            setDebouncedSearchTerm(value);
            setPageNumber(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const totalCandidates = totalCandidatesFromTotals;

    const columns = useMemo(() => createCandidateListColumns(history), [history]);


    return (
        <div>
            <style>{`
                .candidates-table-with-columns .ant-table {
                    border-radius: 16px;
                }
            `}</style>
            <Row style={{ marginBottom: '24px' }} align="middle">
                <Col flex="auto">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Title level={2} style={{ margin: 0 }}>
                            Candidates
                        </Title>
                        <Tooltip title="You're getting early access to this feature! Data and details may update as we refine reliability, and your feedback will help us improve.">
                            <Tag color="gold" style={{ margin: 0, fontWeight: '500', fontSize: '12px' }}>
                                BETA
                            </Tag>
                        </Tooltip>
                    </div>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={8}>
                    <Card 
                        style={{ 
                            textAlign: 'center',
                            backgroundColor: totalsLoading ? '#f5f5f5' : '#4dd0e1',
                            border: 'none',
                            borderRadius: '16px'
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        {totalsLoading ? (
                            <Skeleton 
                                active 
                                paragraph={{ rows: 1, width: ['60%'] }}
                                title={{ width: '80%' }}
                            />
                        ) : (
                            <>
                                <Title level={2} style={{ margin: 0, color: '#fff' }}>{totalCandidates.toLocaleString()}</Title>
                                <Text style={{ color: '#fff', fontSize: '16px' }}>Candidates</Text>
                            </>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card 
                        style={{ 
                            textAlign: 'center',
                            backgroundColor: totalsLoading ? '#f5f5f5' : '#ec407a',
                            border: 'none',
                            borderRadius: '16px'
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        {totalsLoading ? (
                            <Skeleton 
                                active 
                                paragraph={{ rows: 1, width: ['60%'] }}
                                title={{ width: '80%' }}
                            />
                        ) : (
                            <>
                                <Title level={2} style={{ margin: 0, color: '#fff' }}>{totalReferrals.toLocaleString()}</Title>
                                <Text style={{ color: '#fff', fontSize: '16px' }}>Referrals</Text>
                            </>
                        )}
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card 
                        style={{ 
                            textAlign: 'center',
                            backgroundColor: totalsLoading ? '#f5f5f5' : '#7e57c2',
                            border: 'none',
                            borderRadius: '16px'
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        {totalsLoading ? (
                            <Skeleton 
                                active 
                                paragraph={{ rows: 1, width: ['60%'] }}
                                title={{ width: '80%' }}
                            />
                        ) : (
                            <>
                                <Title level={2} style={{ margin: 0, color: '#fff' }}>{totalApplications.toLocaleString()}</Title>
                                <Text style={{ color: '#fff', fontSize: '16px' }}>Applications</Text>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={24} md={8}>
                    <Input
                        placeholder="Search by name or email"
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{ 
                            width: '100%',
                            borderRadius: '4px'
                        }}
                        allowClear
                    />
                </Col>
            </Row>

            <style>
                {`
                    .candidates-table .ant-table-thead > tr > th:first-child {
                        padding-left: 32px !important;
                    }
                `}
            </style>
            
            {error && (
                <Alert
                    message="Error loading candidates"
                    description={error.message}
                    type="error"
                    showIcon
                    style={{ marginBottom: '20px' }}
                />
            )}

            <Table
                className="candidates-table"
                columns={columns}
                dataSource={candidates}
                loading={loading && candidates.length === 0}
                rowKey="id"
                pagination={{
                    current: pageNumber,
                    pageSize: pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) => 
                        `${range[0]}-${range[1]} of ${total} total candidates`,
                    onChange: (page, size) => {
                        setPageNumber(page);
                        if (size !== pageSize) {
                            setPageSize(size);
                            setPageNumber(1);
                        }
                    },
                }}
                size="small"
                style={{
                    backgroundColor: '#fff',
                    borderRadius: '16px',
                    overflow: 'hidden'
                }}
                className="candidates-table-with-columns"
                scroll={{ x: 800 }}
            />
        </div>
    );
}

export default CandidatesListPage;